/**
 * My Profile — the personal profile shared by startups, investors and admins.
 *
 * EDITING IS PER SECTION.
 * -----------------------
 * This screen used to be one global "Edit" toggle: a button flipped every field
 * on the page into an input and a single Save wrote all of them. That shape
 * caused the three defects this rewrite removes.
 *
 *  1. The keyboard closed after every keystroke in Location and LinkedIn. Their
 *     shared input was declared INSIDE this component, so it was a new
 *     component type on every render and React remounted the `TextInput` per
 *     character. Name and Bio were inline JSX and worked — two of four fields
 *     typeable, which read as "strange" rather than broken. Fields now live at
 *     module scope in `components/profile/ProfileSectionFields.tsx`.
 *
 *  2. Saving anything rewrote everything. With sections, a save carries only
 *     that section's fields, so the write had to become a PATCH first — see
 *     `lib/profile/profile-edit-fields.ts`. Without that, saving General info
 *     would have deleted the bio.
 *
 *  3. Validation could only run at submit time and arrived as a blocking dialog
 *     naming a field the user could no longer see. Errors are now inline, next
 *     to the input, and clear as soon as the user edits.
 *
 * Empty values render as an invitation ("Add location") in the accent colour
 * rather than grey "Not set" text sitting in the value slot, where it was
 * indistinguishable from real low-emphasis data.
 */

import { useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Mail,
  MapPin,
  Linkedin,
  User,
  Briefcase,
  Pencil,
  ChevronRight,
  Settings,
  FileText,
  Building2,
} from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import {
  ProfileSectionModal,
  FUNCTION_TITLE_OPTIONS,
  SECTION_TITLES,
} from '@/components/profile/ProfileSectionModal';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSavePersonalProfile } from '@/lib/profile/use-save-personal-profile';
import {
  buildPersonalProfilePatch,
  type PersonalEditSection,
} from '@/lib/profile/profile-edit-fields';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { handleError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { capitalizeWords } from '@/lib/format';
import { PLANS_LINK } from '@/lib/commerce/navigation';

/** One displayed value inside a section card. Module scope — see the header. */
function ProfileReadRow({
  icon,
  label,
  value,
  emptyLabel,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  emptyLabel: string;
}) {
  const colors = useThemeColors();
  const filled = Boolean(value && value.trim());

  return (
    <View className="flex-row items-start gap-3 py-2.5">
      <View className="mt-0.5">{icon}</View>
      <View className="flex-1">
        <Text style={{ color: colors.text.tertiary }} className="text-xs mb-0.5" numberOfLines={1}>
          {label}
        </Text>
        <Text
          // An empty field is an ACTION, not a grey value. "Not set" rendered in
          // the value slot was indistinguishable from real data.
          style={{ color: filled ? colors.text.primary : colors.primary[600] }}
          className={filled ? 'text-base' : 'text-base font-medium'}
          numberOfLines={2}
        >
          {filled ? value : emptyLabel}
        </Text>
      </View>
    </View>
  );
}

/** A titled card with a single Edit affordance covering its rows. */
function ProfileSectionCard({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  const colors = useThemeColors();

  return (
    <View
      className="rounded-2xl p-4 mb-4 overflow-hidden"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <View className="flex-row items-center justify-between mb-1">
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-xs font-semibold uppercase tracking-wide flex-1"
          numberOfLines={1}
        >
          {title}
        </Text>
        <AnimatedPressable
          onPress={onEdit}
          className="flex-row items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ backgroundColor: colors.surface.secondary }}
          accessibilityRole="button"
          accessibilityLabel={`Edit ${title}`}
        >
          <Pencil size={13} color={colors.primary[600]} />
          <Text style={{ color: colors.primary[600] }} className="text-xs font-semibold">
            Edit
          </Text>
        </AnimatedPressable>
      </View>
      {children}
    </View>
  );
}

function QuickLinkRow({
  icon,
  label,
  onPress,
}: {
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
}) {
  const colors = useThemeColors();
  return (
    <AnimatedPressable
      onPress={onPress}
      className="flex-row items-center justify-between px-4 py-4"
      accessibilityRole="button"
    >
      <View className="flex-row items-center gap-3 flex-1">
        <View
          className="w-9 h-9 rounded-xl items-center justify-center"
          style={{ backgroundColor: colors.light[200] }}
        >
          {icon}
        </View>
        <Text style={{ color: colors.text.primary }} className="font-medium flex-1" numberOfLines={1}>
          {label}
        </Text>
      </View>
      <ChevronRight size={20} color={colors.text.tertiary} />
    </AnimatedPressable>
  );
}

export default function EditProfileScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { alert } = useAlert();
  const userRole = useProfileStore((s) => s.userRole);
  const user = useProfileStore((s) => s.user);
  const investorProfile = useProfileStore((s) => s.investorProfile);

  const isStartup = userRole === 'startup';
  const isAdmin = userRole === 'admin';

  const profileHref = isStartup
    ? '/(startup)/userprofile'
    : isAdmin
    ? '/(admin)/profile'
    : '/(investor)/investorprofile';

  const breadcrumbs: Breadcrumb[] = [
    { label: 'Profile', href: profileHref },
    { label: 'My Profile' },
  ];

  const { startupId } = useMyStartup();
  const savePersonalProfile = useSavePersonalProfile(startupId);

  const [openSection, setOpenSection] = useState<PersonalEditSection | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const location = user?.location || investorProfile?.location || '';
  const functionTitle = user?.role || '';
  // The title lives on the caller's own `team_members` row, so it is only
  // showable AND editable when they actually belong to a startup. Showing the
  // row without the field would invite an edit that has nowhere to go.
  const canEditTitle = isStartup && Boolean(startupId);
  const titleLabel =
    FUNCTION_TITLE_OPTIONS.find((o) => o.value === functionTitle)?.label || functionTitle;

  // Seeded from current values so an untouched field is rewritten identically
  // rather than cleared.
  const sectionData: Record<PersonalEditSection, Record<string, string>> = {
    generalInfo: {
      name: user?.name || '',
      functionTitle,
      location,
      linkedIn: user?.linkedIn || '',
    },
    about: { bio: user?.bio || '' },
  };

  const handleSaveSection = async (data: Record<string, string>) => {
    if (!openSection) return;

    // Only this section's fields travel. Anything else is left untouched by the
    // patch — that is the whole point of the split.
    const patch = buildPersonalProfilePatch(openSection, data);

    setIsSaving(true);
    try {
      await savePersonalProfile.mutateAsync(patch);
      setOpenSection(null);
    } catch (err) {
      // A failed save must NOT close the editor or report success.
      const { message, isCancelled } = handleError(err, {
        context: `EditProfile.handleSaveSection.${openSection}`,
      });
      if (!isCancelled) alert("Couldn't save your profile", message);
    } finally {
      setIsSaving(false);
    }
  };

  const content = (
    <>
      {/* Identity */}
      <Animated.View entering={FadeInDown.duration(400)} className="items-center pt-2 pb-6">
        <View
          className="w-24 h-24 rounded-full items-center justify-center mb-3"
          style={{ backgroundColor: colors.primary[100] }}
        >
          <Text style={{ color: colors.primary[700] }} className="text-3xl font-bold">
            {(user?.name || 'U').charAt(0).toUpperCase()}
          </Text>
        </View>
        {/* NOTE: there is deliberately no camera badge here. One existed and had
            no onPress at all — a control that did nothing when tapped. A real
            avatar upload exists in `(startup)/userprofile.tsx`, but only for
            startups; lifting it so investors and admins can use it too is
            tracked separately rather than faked here. */}
        <Text
          style={{ color: colors.text.primary }}
          className="text-2xl font-bold text-center"
          numberOfLines={2}
        >
          {capitalizeWords(user?.name) || 'Your Name'}
        </Text>
        {isStartup && titleLabel ? (
          <View className="mt-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: colors.primary[50] }}>
            <Text style={{ color: colors.primary[600] }} className="text-sm font-medium" numberOfLines={1}>
              {titleLabel}
            </Text>
          </View>
        ) : null}
      </Animated.View>

      {/* General info */}
      <Animated.View entering={FadeInDown.delay(80).duration(400)}>
        <ProfileSectionCard
          title={SECTION_TITLES.generalInfo}
          onEdit={() => setOpenSection('generalInfo')}
        >
          <ProfileReadRow
            icon={<User size={18} color={colors.text.tertiary} />}
            label="Full name"
            value={capitalizeWords(user?.name)}
            emptyLabel="Add your name"
          />
          {canEditTitle && (
            <ProfileReadRow
              icon={<Briefcase size={18} color={colors.text.tertiary} />}
              label="Role at the company"
              value={titleLabel}
              emptyLabel="Add your role"
            />
          )}
          <ProfileReadRow
            icon={<MapPin size={18} color={colors.text.tertiary} />}
            label="Location"
            value={capitalizeWords(location)}
            emptyLabel="Add location"
          />
          <ProfileReadRow
            icon={<Linkedin size={18} color={colors.text.tertiary} />}
            label="LinkedIn"
            value={user?.linkedIn}
            emptyLabel="Add LinkedIn profile"
          />
        </ProfileSectionCard>
      </Animated.View>

      {/* Email — READ ONLY.
          It is the sign-in identity and belongs to the auth service, not to
          `profiles`. Changing it requires confirming the new address, which
          (auth)/confirm-email.tsx already implements via AuthProvider.changeEmail.
          An editable box here accepted input and silently discarded it. */}
      <Animated.View entering={FadeInDown.delay(140).duration(400)}>
        <View
          className="rounded-2xl p-4 mb-4 overflow-hidden"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <Text
            style={{ color: colors.text.tertiary }}
            className="text-xs font-semibold uppercase tracking-wide mb-1"
          >
            Sign-in email
          </Text>
          <View className="flex-row items-center gap-3 py-2.5">
            <Mail size={18} color={colors.text.tertiary} />
            <Text
              style={{ color: colors.text.primary }}
              className="text-base flex-1"
              numberOfLines={1}
            >
              {user?.email || '—'}
            </Text>
          </View>
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            Changing this needs a confirmation email, so it is handled in Account Settings.
          </Text>
        </View>
      </Animated.View>

      {/* About */}
      <Animated.View entering={FadeInDown.delay(200).duration(400)}>
        <ProfileSectionCard title={SECTION_TITLES.about} onEdit={() => setOpenSection('about')}>
          <Text
            style={{ color: user?.bio ? colors.text.secondary : colors.primary[600] }}
            className={user?.bio ? 'text-sm leading-relaxed py-2' : 'text-base font-medium py-2'}
          >
            {user?.bio || 'Add a short introduction'}
          </Text>
        </ProfileSectionCard>
      </Animated.View>

      {/* Quick Links */}
      <Animated.View entering={FadeInDown.delay(260).duration(400)}>
        <Text
          style={{ color: colors.text.secondary }}
          className="text-xs font-semibold uppercase tracking-wide mb-2 ml-1"
        >
          Quick Links
        </Text>
        <View
          className="rounded-2xl overflow-hidden"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <QuickLinkRow
            icon={<Settings size={18} color={colors.text.secondary} />}
            label="Account Settings"
            onPress={() => router.push('/settings')}
          />
          {isStartup ? (
            <>
              <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />
              <QuickLinkRow
                icon={<Building2 size={18} color={colors.primary[600]} />}
                label="Company Profile"
                onPress={() => router.push('/(startup)/company')}
              />
              {PLANS_LINK && (
                <>
                  <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />
                  <QuickLinkRow
                    icon={<FileText size={18} color={colors.text.secondary} />}
                    label={PLANS_LINK.label}
                    onPress={() => PLANS_LINK!.open(router, 'startup')}
                  />
                </>
              )}
            </>
          ) : !isAdmin && PLANS_LINK ? (
            <>
              <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />
              <QuickLinkRow
                icon={<Building2 size={18} color={colors.primary[600]} />}
                label={PLANS_LINK.label}
                onPress={() => PLANS_LINK!.open(router, 'investor')}
              />
            </>
          ) : null}
        </View>
      </Animated.View>
    </>
  );

  const editor = (
    <ProfileSectionModal
      visible={openSection !== null}
      section={openSection}
      initialData={openSection ? sectionData[openSection] : {}}
      // The title lives on `team_members`, so it only has somewhere to go when
      // the caller actually belongs to a startup.
      showFunctionTitle={canEditTitle}
      isSaving={isSaving}
      onClose={() => setOpenSection(null)}
      onSave={handleSaveSection}
    />
  );

  if (isWeb) {
    return (
      <AppLayout title="My Profile" breadcrumbs={breadcrumbs}>
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ padding: 20, maxWidth: 640 }}
        >
          {content}
        </ScrollView>
        {editor}
      </AppLayout>
    );
  }

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top']} className="flex-1">
        <Animated.View
          entering={FadeInDown.duration(400)}
          className="flex-row items-center gap-4 px-5 pt-4 pb-4"
          style={{
            backgroundColor: colors.surface.primary,
            borderBottomWidth: 1,
            borderBottomColor: colors.border.light,
          }}
        >
          <AnimatedPressable
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.surface.secondary }}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <ChevronLeft size={24} color={colors.text.primary} />
          </AnimatedPressable>
          <Text
            style={{ color: colors.text.primary }}
            className="text-xl font-bold flex-1"
            numberOfLines={1}
          >
            My Profile
          </Text>
        </Animated.View>

        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ padding: 20, paddingBottom: 40 }}
        >
          {content}
        </ScrollView>
      </SafeAreaView>
      {editor}
    </View>
  );
}
